import axios from "axios";

// BACKEND_URL constant for making API calls to our FastAPI backend
const BACKEND_URL = "https://api.tradefolio.com";

/**
 * Validates the algo structure to ensure it has proper buy and sell logic
 * 
 * @param {Array|Object} dndRequest - The drag-and-drop request object or array
 * @returns {boolean} - True if the algo is valid, false otherwise
 */
export const algoValidations = (dndRequest) => {
  try {
    // Check if it's an array format
    if (Array.isArray(dndRequest)) {
      // Must have at least two items (buy and sell logic)
      if (dndRequest.length < 2) {
        console.error("[Validation] Algo must have both buy and sell logic");
        return false;
      }

      // Check if we have both buy and sell types
      const buyLogic = dndRequest.find(item => item.type === "BUY");
      const sellLogic = dndRequest.find(item => item.type === "SELL");

      if (!buyLogic || !sellLogic) {
        console.error("[Validation] Algo must have both buy and sell logic");
        return false;
      }

      // Check if both have left and right logic
      if (!buyLogic.leftLogic || !buyLogic.rightLogic || !sellLogic.leftLogic || !sellLogic.rightLogic) {
        console.error("[Validation] Both buy and sell must have left and right logic");
        return false;
      }

      return true;
    } 
    // Check if it's an object format
    else if (dndRequest && typeof dndRequest === 'object') {
      // Must have both buyLogic and sellLogic
      if (!dndRequest.buyLogic || !dndRequest.sellLogic) {
        console.error("[Validation] Algo must have both buy and sell logic");
        return false;
      }

      // Check if both have left and right logic
      if (!dndRequest.buyLogic.leftLogic || !dndRequest.buyLogic.rightLogic || 
          !dndRequest.sellLogic.leftLogic || !dndRequest.sellLogic.rightLogic) {
        console.error("[Validation] Both buy and sell must have left and right logic");
        return false;
      }

      return true;
    }

    console.error("[Validation] Invalid algo structure");
    return false;
  } catch (error) {
    console.error("[Validation] Error validating algo:", error);
    return false;
  }
};

/**
 * Gets stock indicator value from the backend
 * 
 * @param {string} symbol - Stock symbol
 * @param {string} indicator - Indicator name (rsi, etc.)
 * @param {number} period - Period value for the indicator
 * @param {string} interval - Time interval (1d, 4h, etc.)
 * @returns {Promise<Object>} - The indicator value
 */
export const getStockIndicatorValue = async (symbol, indicator, period, interval) => {
  try {
    console.log(`[getStockIndicatorValue] Fetching indicator ${indicator} for ${symbol} with period ${period} and interval ${interval}`);
    
    // Create request payload
    const payload = {
      symbol,
      indicator,
      period: parseInt(period) || 14,
      interval: interval || "1d",
    };
    
    // Call backend API endpoint
    const response = await fetch(`${BACKEND_URL}/indicator`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend returned error: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`[getStockIndicatorValue] Error for ${symbol}:`, error);
    return { value: null, error: error.message };
  }
};

/**
 * Helper to get stock symbol from different formats
 * 
 * @param {Object|string} stock - Stock object or symbol string
 * @returns {string|null} - The stock symbol string or null if invalid
 */
export const getStockSymbol = (stock) => {
  if (!stock) return null;
  
  // If it's already a string, assume it's a symbol
  if (typeof stock === 'string') {
    return stock.toUpperCase();
  }
  
  // If it's an object with a symbol property
  if (typeof stock === 'object' && stock !== null) {
    if (stock.symbol) {
      return stock.symbol.toUpperCase();
    }
    // Try alternate property names
    if (stock.Symbol) {
      return stock.Symbol.toUpperCase();
    }
    if (stock.ticker) {
      return stock.ticker.toUpperCase();
    }
    if (stock.Ticker) {
      return stock.Ticker.toUpperCase();
    }
  }
  
  // Invalid input
  return null;
};

/**
 * Gets indicator values for a stock (now calls backend API instead of TAAPI directly)
 * 
 * @param {Object} req - The request object containing logic
 * @param {string} symbol - Stock symbol
 * @param {string} indicator - Indicator name
 * @param {string|number} period - Time period for indicator
 * @param {string} interval - Time interval for indicator
 * @returns {Promise<Object>} - Indicator values from backend
 */
export const getIndicatorAPI = async (req, symbol, indicator, period, interval) => {
  try {
    console.log(`[Indicator API] Fetching indicator data from backend for ${symbol}, ${indicator}`);
    
    // Create request payload
    const payload = {
      symbol,
      indicator,
      period: parseInt(period) || 14,
      interval: interval || "1d",
    };
    
    // Call backend API endpoint
    const response = await fetch(`${BACKEND_URL}/indicator`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend returned error: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`[Indicator API] Error getting indicator for ${symbol}:`, error);
    // Return a format that the existing code expects
    return {
      value: null,
      error: error.message
    };
  }
};

/**
 * Gets candle data for a stock (now calls backend API instead of TAAPI directly)
 * 
 * @param {Object} req - The request object containing logic
 * @param {string} symbol - Stock symbol
 * @param {boolean} isOriginal - Whether to return original format
 * @returns {Promise<Object>} - Candle data from backend
 */
export const getLeftIndicatorAPI = async (req, symbol, isOriginal = false) => {
  try {
    console.log(`[Left Indicator API] Fetching candle data from backend for ${symbol}`);
    
    // Extract interval from request object
    const interval = req?.leftLogic?.interval || req?.interval || "1d";
    
    // Create request payload
    const payload = {
      symbol,
      interval,
      limit: 100 // Number of candles to fetch
    };
    
    // Call backend API endpoint
    const response = await fetch(`${BACKEND_URL}/candles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend returned error: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    
    // Format the response to match what the frontend expects
    return {
      data: result.candles || [],
      indicator: req?.leftLogic?.componentName || req?.componentName,
      interval: interval,
      status: "success"
    };
  } catch (error) {
    console.error(`[Left Indicator API] Error getting candles for ${symbol}:`, error);
    // Return a format that the existing code expects
    return {
      data: [],
      error: error.message,
      status: "error"
    };
  }
};

/**
 * Runs a backtest by sending data to the Python backend API
 * 
 * @param {Object|Array} dndRequest - The drag-and-drop request structure containing buy and sell logic
 * @param {Array} selectedStockList - List of stocks to run the backtest on
 * @returns {Object} - Backtest results from the backend
 */
export const backtest = async (dndRequest, selectedStockList) => {
  try {
    console.log(`[Backtest] Starting backtest with request structure:`, JSON.stringify(dndRequest, null, 2));
    
    // Check if dndRequest has the expected structure
    const isBuyRequestArray = Array.isArray(dndRequest);
    
    // Extract buy and sell logic based on structure
    let buyReq, sellReq;
    
    if (isBuyRequestArray) {
      // Handle original array format [buyObj, sellObj]
      buyReq = dndRequest?.filter((item) => item?.type === "BUY")[0];
      sellReq = dndRequest?.filter((item) => item?.type === "SELL")[0];
      console.log(`[Backtest] Using array format request structure, found buyReq: ${!!buyReq}, sellReq: ${!!sellReq}`);
    } else {
      // Handle new object format {buyLogic, sellLogic}
      buyReq = dndRequest?.buyLogic;
      sellReq = dndRequest?.sellLogic;
      console.log(`[Backtest] Using object format request structure, found buyReq: ${!!buyReq}, sellReq: ${!!sellReq}`);
    }
    
    // If we still don't have valid request objects, log error and return
    if (!buyReq || !sellReq) {
      console.error(`[Backtest] Invalid request structure. Missing buy or sell logic.`, dndRequest);
      return { error: "Invalid request structure" };
    }
    
    // Process stock list to ensure we have clean stock symbols
    const processedStockList = selectedStockList.map(stock => {
      // If stock is an object with a symbol property, extract it
      if (stock && typeof stock === 'object' && stock.symbol) {
        return stock.symbol;
      }
      // If stock is already a string, use it directly
      else if (typeof stock === 'string') {
        return stock;
      }
      // For any other format, try to convert to string
      else if (stock) {
        return String(stock);
      }
      // Skip invalid items
      return null;
    }).filter(Boolean); // Remove any null/undefined values
    
    console.log(`[Backtest] Processed stock list: ${JSON.stringify(processedStockList)}`);
    
    // Only proceed if we have valid stock symbols
    if (processedStockList.length === 0) {
      console.error('[Backtest] No valid stock symbols found in selectedStockList');
      return { transactions: [] };
    }
    
    // Extract necessary parameters from buy and sell logic
    const extractParams = (req) => {
      let indicator, interval, period, threshold;
      
      if (req.leftLogic && typeof req.leftLogic === 'object') {
        indicator = req.leftLogic.componentName?.toLowerCase();
        interval = req.leftLogic.interval || '1h';
        period = req.leftLogic.timeRange || '14';
      } else {
        // Fallback to component name directly if available
        indicator = req.componentName?.toLowerCase();
        interval = req.interval || '1h';
        period = req.timeRange || '14';
      }
      
      // Extract threshold
      if (req.rightLogic && typeof req.rightLogic === 'object') {
        threshold = req.rightLogic.value;
        if (typeof threshold === 'object' && threshold !== null) {
          threshold = threshold.value;
        }
      }
      
      return { indicator, interval, period, threshold };
    };
    
    const buyParams = extractParams(buyReq);
    const sellParams = extractParams(sellReq);
    
    console.log(`[Backtest] Buy parameters: ${JSON.stringify(buyParams)}`);
    console.log(`[Backtest] Sell parameters: ${JSON.stringify(sellParams)}`);
    
    // Create common request payload settings to be used for all stocks
    const commonPayloadSettings = {
      timeframe: buyParams.interval || "1d",
      period: parseInt(buyParams.period) || 14,
      upper_threshold: parseFloat(sellParams.threshold) || 70,
      lower_threshold: parseFloat(buyParams.threshold) || 30,
      initial_capital: 10000,
      position_size: 0.1,  // 10%
      commission: 0.001    // 0.1%
    };
    
    // Run backtests for each stock in parallel
    const backtestResults = await Promise.all(processedStockList.map(async (symbol) => {
      try {
        console.log(`[Backtest] Sending backtest request to backend for symbol: ${symbol}`);
        
        // Create request payload
        const payload = {
          symbol,
          ...commonPayloadSettings
        };
        
        // Send request to backend
        const response = await fetch(`${BACKEND_URL}/backtest`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Backend returned error: ${response.status} ${errorText}`);
        }

        // Parse the response
        const result = await response.json();
        
        // Check if the result contains an error
        if (result.error) {
          console.error(`[Backtest] Server returned error:`, result.error);
          throw new Error(result.error);
        }
        
        // Return the result directly from the backend
        return {
          symbol,
          ...result,
          error: false
        };
      } catch (error) {
        console.error(`[Backtest] Error processing stock ${symbol}:`, error);
        return {
          symbol: symbol,
          error: error.message
        };
      }
    }));
    
    // Filter out failures
    const validResults = backtestResults.filter(result => !result.error);
    console.log(`[Backtest] Processed ${validResults.length} valid results out of ${processedStockList.length} stocks`);
    
    // Get the first valid result for detailed execution information
    const firstValidResult = validResults.length > 0 ? validResults[0] : null;
    
    console.log('===== BACKTEST DATA DEBUG =====');
    console.log('First valid result:', firstValidResult);
    console.log('First valid result - raw:', JSON.stringify(firstValidResult));
    console.log('Has performance data:', !!firstValidResult?.performance);
    console.log('Has trades data:', !!firstValidResult?.trades);
    console.log('Has dates data:', !!firstValidResult?.dates);
    console.log('Has rsi_values data:', !!firstValidResult?.rsi_values);
    if (firstValidResult?.performance) {
      console.log('Performance data keys:', Object.keys(firstValidResult.performance));
      console.log('Performance sample values:', {
        total_return: firstValidResult.performance.total_return,
        total_return_pct: firstValidResult.performance.total_return_pct,
        win_rate: firstValidResult.performance.win_rate
      });
      console.log('Raw backend performance values:', {
        win_rate: firstValidResult.performance.win_rate,
        total_return_pct: firstValidResult.performance.total_return_pct,
        max_drawdown_pct: firstValidResult.performance.max_drawdown_pct
      });
    }
    
    const finalResult = {
      result: backtestResults,
      backtestResult: {
        stocks: processedStockList,
        // Include the detailed execution log for the frontend to display
        detailedExecutionLog: firstValidResult ? {
          initialization: {
            symbol: firstValidResult.symbol,
            initialCapital: firstValidResult.initial_capital,
            rsiPeriod: firstValidResult.parameters?.period || commonPayloadSettings.period,
            rsiLowerThreshold: firstValidResult.parameters?.lower_threshold || commonPayloadSettings.lower_threshold,
            rsiUpperThreshold: firstValidResult.parameters?.upper_threshold || commonPayloadSettings.upper_threshold,
            positionSize: firstValidResult.parameters?.position_size || (commonPayloadSettings.position_size || 0.1),
            commissionRate: firstValidResult.parameters?.commission || (commonPayloadSettings.commission || 0.001)
          },
          dataProcessing: {
            daysProcessed: firstValidResult.dates?.length || 0,
            dataPoints: {
              price: firstValidResult.dates?.length || 0,
              rsi: firstValidResult.rsi_values?.length || 0
            },
            dateRange: {
              start: firstValidResult.dates?.length > 0 ? firstValidResult.dates[0] : null,
              end: firstValidResult.dates?.length > 0 ? firstValidResult.dates[firstValidResult.dates.length - 1] : null
            }
          },
          signals: firstValidResult.trades ? firstValidResult.trades.reduce((acc, trade, index, allTrades) => {
            // Restructure trade data to match expected format for Backtest.js
            if (trade.type === 'BUY') {
              // Find the corresponding SELL if it exists
              const sellTrade = allTrades.find((t, i) => i > index && t.type === 'SELL');
              
              // Calculate trade result if we have both buy and sell
              let result = null;
              if (sellTrade) {
                const profit = sellTrade.price - trade.price;
                const percentProfit = (profit / trade.price) * 100;
                result = {
                  profit: profit,
                  percentProfit: percentProfit
                };
              }
              
              // Add to signals array
              acc.push({
                buy: {
                  date: trade.date,
                  price: trade.price,
                  rsi: trade.rsi,
                  shares: trade.shares || (firstValidResult.initial_capital * (commonPayloadSettings.position_size || 0.1) / trade.price)
                },
                sell: sellTrade ? {
                  date: sellTrade.date,
                  price: sellTrade.price,
                  rsi: sellTrade.rsi,
                  shares: sellTrade.shares || (firstValidResult.initial_capital * (commonPayloadSettings.position_size || 0.1) / trade.price)
                } : null,
                result: result
              });
            }
            return acc;
          }, []) : [],
          performance: firstValidResult.performance || {},
          basicSummary: {
            // Map performance metrics from the backend to the expected frontend format
            totalProfit: firstValidResult.performance?.total_return_pct || 0,
            totalReturn: firstValidResult.performance?.total_return || 0,
            annualizedReturn: firstValidResult.performance?.annualized_return_pct || 0,
            maxDrawdownPercent: firstValidResult.performance?.max_drawdown_pct || 0,
            biggestWinPercent: firstValidResult.performance?.biggest_win_pct || 0,
            biggestLossPercent: firstValidResult.performance?.biggest_loss_pct || 0,
            tradeCount: firstValidResult.performance?.total_trades || 0,
            winRate: firstValidResult.performance?.win_rate || 0
          },
          executionSummary: {
            status: "success",
            message: "Backtest completed successfully",
            tradingRulesSummary: `Using RSI crossing strategy with RSI period ${commonPayloadSettings.period}, buying when RSI falls below ${commonPayloadSettings.lower_threshold} and selling when RSI rises above ${commonPayloadSettings.upper_threshold}.`
          }
        } : null
      }
    };
    
    console.log('===== TRANSFORMATION DEBUG =====');
    console.log('Final result structure:', {
      hasResult: true,
      hasBacktestResult: true,
      hasDetailedExecutionLog: !!finalResult.backtestResult.detailedExecutionLog,
      detailedExecutionLogKeys: finalResult.backtestResult.detailedExecutionLog ? Object.keys(finalResult.backtestResult.detailedExecutionLog) : []
    });
    
    if (finalResult.backtestResult.detailedExecutionLog) {
      console.log('DetailedExecutionLog initialization:', finalResult.backtestResult.detailedExecutionLog.initialization);
      console.log('DetailedExecutionLog dataProcessing:', finalResult.backtestResult.detailedExecutionLog.dataProcessing);
      console.log('DetailedExecutionLog signals count:', finalResult.backtestResult.detailedExecutionLog.signals.length);
      console.log('DetailedExecutionLog has performance:', !!finalResult.backtestResult.detailedExecutionLog.performance);
      console.log('DetailedExecutionLog has basicSummary:', !!finalResult.backtestResult.detailedExecutionLog.basicSummary);
    }
    
    return finalResult;
  } catch (error) {
    console.log('Error in backtest', error);
    return { error: error.message };
  }
}

/**
 * Calculates the profit from a buy and sell transaction
 * 
 * @param {Object} buyTxnObj - Buy transaction object
 * @param {Object} sellTxnObj - Sell transaction object
 * @param {number} shares - Number of shares
 * @returns {Object} - Profit calculation result
 */
export const calculateProfit = (buyTxnObj, sellTxnObj, shares) => {
  try {
    const buyPrice = buyTxnObj?.result?.price || 0;
    const sellPrice = sellTxnObj?.result?.price || 0;
    const profit = (sellPrice - buyPrice) * shares;
    const percentProfit = ((sellPrice - buyPrice) / buyPrice) * 100;
    
    return {
      profit: profit.toFixed(2),
      percentProfit: percentProfit.toFixed(2) + '%'
    };
  } catch (error) {
    console.error("Error calculating profit:", error);
    return {
      profit: 0,
      percentProfit: '0%'
    };
  }
};

/**
 * Extracts the price from a candle object or transaction object
 * 
 * @param {Object} obj - Candle or transaction object
 * @returns {number} - Price value
 */
export const getPriceFromCandle = (obj) => {
  // If the object has a direct price property
  if (obj?.price) {
    return obj.price;
  }
  
  // If the object has a result with price
  if (obj?.result?.price) {
    return obj.result.price;
  }
  
  // If it's a candle object with close price
  if (obj?.close) {
    return obj.close;
  }
  
  // If it's a nested structure
  if (obj?.result?.close) {
    return obj.result.close;
  }
  
  // Default fallback
  return 0;
};

/**
 * Calculates the backtest result from a list of transactions
 * 
 * @param {Array} transactions - List of transactions
 * @returns {Object} - Backtest result
 */
export const calculateBackTestResult = (transactions) => {
  try {
    if (!transactions || transactions.length === 0) {
      return {
        status: false,
        message: "No transactions found"
      };
    }

    let totalShares = 0;
    let totalBuyAmount = 0;
    let totalSellAmount = 0;
    let totalProfitLoss = 0;
    let totalPercentChange = 0;
    let successfulTrades = 0;
    let failedTrades = 0;
    let tradeDetails = [];
    let maxProfit = 0;
    let maxLoss = 0;
    let longestHoldingPeriodMs = 0;
    let shortestHoldingPeriodMs = Number.MAX_SAFE_INTEGER;
    
    transactions.forEach((transaction, index) => {
      const { buyTxnObj, sellTxnObj, stock, profitCalc } = transaction;
      
      // Only count transactions where both buy and sell were successful
      if (buyTxnObj?.success && sellTxnObj?.success) {
        const buyPrice = getPriceFromCandle(buyTxnObj);
        const sellPrice = getPriceFromCandle(sellTxnObj);
        const shares = buyTxnObj?.buyQty || 0;
        
        if (buyPrice && sellPrice && shares) {
          // Calculate trade metrics
          const buyAmount = buyPrice * shares;
          const sellAmount = sellPrice * shares;
          const profitLoss = sellAmount - buyAmount;
          const percentChange = ((sellPrice - buyPrice) / buyPrice) * 100;
          
          // Calculate holding period if timestamps are available
          let holdingPeriodMs = 0;
          if (buyTxnObj?.result?.timestamp && sellTxnObj?.result?.timestamp) {
            holdingPeriodMs = new Date(sellTxnObj.result.timestamp) - new Date(buyTxnObj.result.timestamp);
            longestHoldingPeriodMs = Math.max(longestHoldingPeriodMs, holdingPeriodMs);
            shortestHoldingPeriodMs = Math.min(shortestHoldingPeriodMs, holdingPeriodMs);
          }
          
          // Update accumulators
          totalShares += shares;
          totalBuyAmount += buyAmount;
          totalSellAmount += sellAmount;
          totalProfitLoss += profitLoss;
          totalPercentChange += percentChange;
          successfulTrades++;
          
          // Track max profit/loss
          maxProfit = Math.max(maxProfit, profitLoss > 0 ? profitLoss : 0);
          maxLoss = Math.min(maxLoss, profitLoss < 0 ? profitLoss : 0);
          
          // Store detailed trade info
          tradeDetails.push({
            symbol: stock,
            buyDate: buyTxnObj?.result?.timestamp ? new Date(buyTxnObj.result.timestamp).toLocaleString() : 'Unknown',
            sellDate: sellTxnObj?.result?.timestamp ? new Date(sellTxnObj.result.timestamp).toLocaleString() : 'Unknown',
            buyPrice,
            sellPrice,
            shares,
            profitLoss: profitLoss.toFixed(2),
            percentChange: percentChange.toFixed(2) + '%',
            holdingPeriod: holdingPeriodMs ? `${Math.floor(holdingPeriodMs / (1000 * 60 * 60 * 24))} days` : 'Unknown'
          });
        }
      } else {
        failedTrades++;
      }
    });
    
    // Calculate average metrics
    const averageProfitLoss = successfulTrades > 0 ? totalProfitLoss / successfulTrades : 0;
    const averagePercentChange = successfulTrades > 0 ? totalPercentChange / successfulTrades : 0;
    
    // Format holding periods for display
    const formatHoldingPeriod = (ms) => {
      if (ms === 0) return 'Unknown';
      if (ms === Number.MAX_SAFE_INTEGER) return 'N/A';
      
      const days = Math.floor(ms / (1000 * 60 * 60 * 24));
      const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      return `${days} days, ${hours} hours`;
    };
    
    return {
      status: true,
      totalTransactions: transactions.length,
      successfulTrades,
      failedTrades,
      totalShares,
      totalBuyAmount: totalBuyAmount.toFixed(2),
      totalSellAmount: totalSellAmount.toFixed(2),
      totalProfitLoss: totalProfitLoss.toFixed(2),
      averageProfitLoss: averageProfitLoss.toFixed(2),
      averagePercentChange: averagePercentChange.toFixed(2) + '%',
      maxProfit: maxProfit.toFixed(2),
      maxLoss: maxLoss.toFixed(2),
      longestHoldingPeriod: formatHoldingPeriod(longestHoldingPeriodMs),
      shortestHoldingPeriod: formatHoldingPeriod(shortestHoldingPeriodMs),
      tradeDetails
    };
  } catch (error) {
    console.error("Error calculating backtest results:", error);
    return {
      status: false,
      message: "Error calculating results: " + error.message
    };
  }
};
